import React, { useReducer, useContext, useEffect } from 'react';
// --- These imports will now work ---
import { CartContext, initialState, cartReducer } from './CartContext';
import { useNotification } from './NotificationContext';

// This is the (correct) "safe load" function
const loadInitialState = (defaultState) => {
  try {
    const storedCart = localStorage.getItem('cart');
    if (!storedCart) return defaultState;
    const parsedCart = JSON.parse(storedCart);
    return Array.isArray(parsedCart) ? parsedCart : defaultState;
  } catch (error) {
    console.error("Failed to parse cart from localStorage, resetting.", error);
    return defaultState;
  }
};

export const CartProvider = ({ children }) => {
  const [cart, dispatch] = useReducer(cartReducer, initialState, loadInitialState);
  const { showNotification } = useNotification();

  // This hook for saving to localStorage is correct
  useEffect(() => {
    if (cart) {
      localStorage.setItem('cart', JSON.stringify(cart));
    }
  }, [cart]);


  // --- "Smart" addItem with stock logic ---
  const addItem = (item) => {
    const stock = item.stock_quantity;
    const quantityToAdd = item.quantity;

    const existingItem = cart.find((i) => i.id === item.id);

    if (existingItem) {
      const newQuantity = existingItem.quantity + quantityToAdd;
      if (newQuantity > stock) {
        showNotification(`Cannot add more. Only ${stock} in stock.`, 'error');
        return;
      }
    } else {
      if (quantityToAdd > stock) {
        showNotification(`Cannot add ${quantityToAdd}. Only ${stock} in stock.`, 'error');
        return;
      }
    }

    dispatch({ type: 'ADD_ITEM', payload: item });
    showNotification(`${quantityToAdd} x ${item.name} added to cart!`, 'success');
  };


  // --- "Smart" updateQuantity with stock logic ---
  const updateQuantity = (item, quantity) => {
    const newQuantity = parseInt(quantity, 10);

    const itemInState = cart.find((i) => i.id === item.id);
    if (!itemInState) return;

    const stock = itemInState.stock_quantity;

    if (newQuantity < 1) {
      dispatch({ type: 'REMOVE_ITEM', payload: item });
      return;
    }

    if (newQuantity > stock) {
      showNotification(`Cannot set quantity. Only ${stock} in stock.`, 'error');
      dispatch({ type: 'UPDATE_QUANTITY', payload: { ...item, quantity: stock } });
      return;
    }

    dispatch({ type: 'UPDATE_QUANTITY', payload: { ...item, quantity: newQuantity } });
  };


  // --- "Dumb" functions that just dispatch ---
  const removeItem = (item) => {
    dispatch({ type: 'REMOVE_ITEM', payload: item });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };


  return (
    <CartContext.Provider
      value={{ cart, addItem, removeItem, updateQuantity, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Step 5: Create a custom hook to use the cart context
export const useCart = () => useContext(CartContext);
